<script setup lang="ts">
  const {
    multiple = false,
    options,
    ariaLabel = "",
  } = defineProps<{
    multiple?: boolean;
    options: Array<{ label: string; value: string | number }>;
    ariaLabel?: string;
  }>();

  const modelValue = defineModel<string | number | Array<string | number>>("modelValue");

  // Track the currently focused option index
  const focusedIndex = ref(0);
  const optionRefs = ref<HTMLElement[]>([]);

  const isSelected = (value: string | number): boolean => {
    if (Array.isArray(modelValue.value)) {
      return modelValue.value.includes(value);
    }
    return modelValue.value === value;
  };

  const toggleOption = (value: string | number, event?: Event) => {
    if (multiple) {
      if (Array.isArray(modelValue.value)) {
        if (modelValue.value.includes(value)) {
          modelValue.value = modelValue.value.filter((v) => v !== value);
        } else {
          modelValue.value = [...modelValue.value, value];
        }
      } else {
        modelValue.value = [value];
      }
    } else {
      modelValue.value = value;
    }

    // Blur the button after selection if event is provided
    if (event) {
      nextTick(() => {
        (event.currentTarget as HTMLElement).blur();
      });
    }
  };

  const handleKeyDown = (event: KeyboardEvent, index: number) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusedIndex.value = Math.min(index + 1, options.length - 1);
        nextTick(() => {
          optionRefs.value[focusedIndex.value]?.focus();
        });
        break;
      case "ArrowUp":
        event.preventDefault();
        focusedIndex.value = Math.max(index - 1, 0);
        nextTick(() => {
          optionRefs.value[focusedIndex.value]?.focus();
        });
        break;
      case "Home":
        event.preventDefault();
        focusedIndex.value = 0;
        nextTick(() => {
          optionRefs.value[0]?.focus();
        });
        break;
      case "End":
        event.preventDefault();
        focusedIndex.value = options.length - 1;
        nextTick(() => {
          optionRefs.value[focusedIndex.value]?.focus();
        });
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        toggleOption(options[index].value);
        break;
    }
  };

  const setOptionRef = (el: HTMLElement | null, index: number) => {
    if (el) {
      optionRefs.value[index] = el;
    }
  };
</script>

<template>
  <div
    role="listbox"
    :aria-label="ariaLabel"
    :aria-multiselectable="multiple ? 'true' : undefined"
    class="flex flex-col gap-2"
  >
    <button
      v-for="(option, index) in options"
      :key="option.value"
      :ref="(el) => setOptionRef(el, index)"
      type="button"
      role="option"
      :aria-selected="isSelected(option.value)"
      :tabindex="index === 0 ? 0 : -1"
      class="group hover:bg-bgr-hover focus:bg-bgr-default focus:border-bdr-focus relative flex h-7 w-full cursor-pointer items-center gap-2 rounded-md border border-transparent px-3 transition-colors"
      :class="{
        'bg-bgr-active': isSelected(option.value),
      }"
      @click="toggleOption(option.value, $event)"
      @keydown="handleKeyDown($event, index)"
    >
      <span
        class="text-body-default text-fgr-default group-hover:text-fgr-hover flex-1 text-left transition-colors"
        :class="{
          'text-body-default-m text-fgr-active': isSelected(option.value),
        }"
      >
        {{ option.label }}
      </span>
      <Icon v-if="isSelected(option.value)" src="check" />
    </button>
  </div>
</template>
